export interface DecodedJwt {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signature: string;
  parts: [string, string, string];
}

export type AlgorithmFamily =
  | 'hmac'
  | 'rsa'
  | 'ecdsa'
  | 'rsapss'
  | 'unsupported';

export interface AlgorithmInfo {
  family: AlgorithmFamily;
  hash: string;
  label: string;
}

const ALGORITHM_MAP: Record<string, AlgorithmInfo> = {
  HS256: { family: 'hmac', hash: 'SHA-256', label: 'HS256' },
  HS384: { family: 'hmac', hash: 'SHA-384', label: 'HS384' },
  HS512: { family: 'hmac', hash: 'SHA-512', label: 'HS512' },
  RS256: { family: 'rsa', hash: 'SHA-256', label: 'RS256' },
  RS384: { family: 'rsa', hash: 'SHA-384', label: 'RS384' },
  RS512: { family: 'rsa', hash: 'SHA-512', label: 'RS512' },
  ES256: { family: 'ecdsa', hash: 'SHA-256', label: 'ES256' },
  ES384: { family: 'ecdsa', hash: 'SHA-384', label: 'ES384' },
  ES512: { family: 'ecdsa', hash: 'SHA-512', label: 'ES512' },
  PS256: { family: 'rsapss', hash: 'SHA-256', label: 'PS256' },
  PS384: { family: 'rsapss', hash: 'SHA-384', label: 'PS384' },
  PS512: { family: 'rsapss', hash: 'SHA-512', label: 'PS512' },
};

/** Look up algorithm details from the JWT `alg` header claim. */
export function getAlgorithmInfo(alg: unknown): AlgorithmInfo {
  if (typeof alg === 'string') {
    const info = ALGORITHM_MAP[alg];
    if (info) {
      return info;
    }
  }

  return {
    family: 'unsupported',
    hash: '',
    label: typeof alg === 'string' ? alg : 'unknown',
  };
}

/**
 * Decode a base64url-encoded string to UTF-8 text.
 * Handles URL-safe characters and missing padding.
 */
export function base64urlDecode(input: string): string {
  const bytes = base64urlToBuffer(input);
  return new TextDecoder().decode(bytes);
}

/** Decode a base64url string to a Uint8Array. */
function base64urlToBuffer(input: string): Uint8Array {
  let base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = base64.length % 4;
  if (padding === 2) {
    base64 += '==';
  } else if (padding === 3) {
    base64 += '=';
  }

  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

/**
 * Parse a JWT string into its three decoded parts.
 * Throws descriptive errors for malformed tokens.
 */
export function decodeJwt(token: string): DecodedJwt {
  const trimmed = token.trim();

  if (!trimmed) {
    throw new Error('Token is empty');
  }

  const dotIndex1 = trimmed.indexOf('.');
  const dotIndex2 = trimmed.indexOf('.', dotIndex1 + 1);

  if (
    dotIndex1 === -1 ||
    dotIndex2 === -1 ||
    trimmed.includes('.', dotIndex2 + 1)
  ) {
    throw new Error(
      'Invalid JWT format: expected 3 segments separated by dots',
    );
  }

  const headerPart = trimmed.slice(0, dotIndex1);
  const payloadPart = trimmed.slice(dotIndex1 + 1, dotIndex2);
  const signaturePart = trimmed.slice(dotIndex2 + 1);

  let headerJson: string;
  try {
    headerJson = base64urlDecode(headerPart);
  } catch {
    throw new Error('Unable to decode header: invalid base64url encoding');
  }

  let header: Record<string, unknown>;
  try {
    header = JSON.parse(headerJson);
  } catch {
    throw new Error('Unable to parse header: invalid JSON');
  }

  let payloadJson: string;
  try {
    payloadJson = base64urlDecode(payloadPart);
  } catch {
    throw new Error('Unable to decode payload: invalid base64url encoding');
  }

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(payloadJson);
  } catch {
    throw new Error('Unable to parse payload: invalid JSON');
  }

  return {
    header,
    payload,
    signature: signaturePart,
    parts: [headerPart, payloadPart, signaturePart],
  };
}

/** Strip PEM headers/footers and decode the base64 body to an ArrayBuffer. */
function pemToBuffer(pem: string): ArrayBuffer {
  const body = pem
    .replace(/-----BEGIN [A-Z ]+-----/, '')
    .replace(/-----END [A-Z ]+-----/, '')
    .replace(/\s/g, '');
  const binaryString = atob(body);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

/** Type guard for JsonWebKey — checks for required `kty` field. */
const isJsonWebKey = (value: unknown): value is JsonWebKey =>
  typeof value === 'object' && value !== null && 'kty' in value;

/** Try to parse a string as a JWK. Returns null if not valid JSON or missing `kty`. */
function tryParseJwk(input: string): JsonWebKey | null {
  try {
    const parsed: unknown = JSON.parse(input.trim());
    if (isJsonWebKey(parsed)) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/** Map hash algorithm to the ECDSA named curve. */
function ecdsaCurve(hash: string): string {
  switch (hash) {
    case 'SHA-384':
      return 'P-384';
    case 'SHA-512':
      return 'P-521';
    default:
      return 'P-256';
  }
}

/** Map hash algorithm to RSA-PSS salt length in bytes. */
function pssSaltLength(hash: string): number {
  switch (hash) {
    case 'SHA-384':
      return 48;
    case 'SHA-512':
      return 64;
    default:
      return 32;
  }
}

/** Import a public key from a PEM (SPKI) or JWK string. */
async function importPublicKey(
  keyInput: string,
  alg: AlgorithmInfo,
): Promise<CryptoKey> {
  let algorithm: RsaHashedImportParams | EcKeyImportParams;
  if (alg.family === 'rsa') {
    algorithm = { name: 'RSASSA-PKCS1-v1_5', hash: alg.hash };
  } else if (alg.family === 'rsapss') {
    algorithm = { name: 'RSA-PSS', hash: alg.hash };
  } else {
    algorithm = { name: 'ECDSA', namedCurve: ecdsaCurve(alg.hash) };
  }

  const jwk = tryParseJwk(keyInput);
  if (jwk) {
    return crypto.subtle.importKey('jwk', jwk, algorithm, false, ['verify']);
  }

  return crypto.subtle.importKey(
    'spki',
    pemToBuffer(keyInput),
    algorithm,
    false,
    ['verify'],
  );
}

/**
 * Verify a JWT signature using the Web Crypto API.
 *
 * For HMAC algorithms, `secretOrKey` is the shared secret string.
 * For RSA/ECDSA/RSA-PSS, `secretOrKey` is a PEM public key (SPKI format)
 * or a JWK string.
 *
 * Returns true if valid, false otherwise. Never throws.
 */
export async function verifySignature(
  token: string,
  secretOrKey: string,
  alg: AlgorithmInfo,
): Promise<boolean> {
  try {
    const trimmed = token.trim();
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot === -1) {
      return false;
    }

    const signingInput = trimmed.slice(0, lastDot);
    const signatureB64 = trimmed.slice(lastDot + 1);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(signingInput);
    const signatureBuffer = base64urlToBuffer(signatureB64);

    if (alg.family === 'hmac') {
      const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(secretOrKey),
        { name: 'HMAC', hash: alg.hash },
        false,
        ['verify'],
      );
      return await crypto.subtle.verify(
        'HMAC',
        key,
        signatureBuffer,
        dataBuffer,
      );
    }

    const key = await importPublicKey(secretOrKey, alg);

    let verifyAlgorithm: AlgorithmIdentifier | RsaPssParams | EcdsaParams;
    if (alg.family === 'rsa') {
      verifyAlgorithm = 'RSASSA-PKCS1-v1_5';
    } else if (alg.family === 'rsapss') {
      verifyAlgorithm = {
        name: 'RSA-PSS',
        saltLength: pssSaltLength(alg.hash),
      };
    } else {
      verifyAlgorithm = { name: 'ECDSA', hash: alg.hash };
    }

    return await crypto.subtle.verify(
      verifyAlgorithm,
      key,
      signatureBuffer,
      dataBuffer,
    );
  } catch {
    return false;
  }
}
